import { useLazyGetPlanQuery } from '@/core/api/graphql/forshawGraphqlApi.generated.ts'
import { getPlanMonthList } from '@/core/utils/getPlanMonthList.ts'
import { useAccountBranchIdsParams } from '@/core/hooks/useAccountBranchIdsParams.ts'
import {
  ForecastItem,
  PlanItem,
  PlannedItem,
  Product,
  ShipmentItem,
  SubmittedItem,
} from '../slice/replenishmentPlan.types.ts'
import { replenishmentPlanActions } from '../slice/replenishmentPlan.slice.ts'
import { useDispatch } from 'react-redux'
import { mapReplenishmentPlanItemFragment } from '@/data/replenishmentPlan/mappers/mapReplenishmentPlanItemFragment.ts'
import { useCallback } from 'react'
import { useGetPlanItemsForecastsShipments } from '@/data/replenishmentPlan/hooks/useGetPlanItemsForecastsShipments.ts'
import { useGetSubmittedItems } from '@/data/replenishmentPlan/hooks/useGetSubmittedItems.ts'

const useFetchAndSavePlan = () => {
  const { branchId } = useAccountBranchIdsParams()

  const { fromMonthFormatted, toMonthFormatted } = getPlanMonthList()

  const dispatch = useDispatch()

  const [getPlan] = useLazyGetPlanQuery()

  const { getPlanItemsForecastsShipments } = useGetPlanItemsForecastsShipments()
  const { getSubmittedItems } = useGetSubmittedItems()

  const fetchAndSavePlan = useCallback(async () => {
    dispatch(replenishmentPlanActions.setIsLoadingPlan(true))

    try {
      const planData = await getPlan({ branchId }).unwrap()

      if (!planData) {
        return
      }

      const { replenishmentPlan } = planData

      if (!replenishmentPlan) {
        return
      }

      const { planItems, forecasts, shipments } =
        await getPlanItemsForecastsShipments({
          branchId,
          fromMonth: fromMonthFormatted,
          toMonth: toMonthFormatted,
        })

      const planProducts: Product[] = []
      let forecastItems: PlanItem<ForecastItem> = {}
      let plannedItems: PlanItem<PlannedItem> = {}
      let shipmentItems: PlanItem<ShipmentItem[]> = {}

      for (const salableProduct of replenishmentPlan) {
        const productsForecast = forecasts.filter(
          (forecast) => forecast.productId === salableProduct.product.id,
        )
        const productsPlanItems = planItems.filter(
          (planItem) => planItem.productId === salableProduct.product.id,
        )
        const productsShipments = shipments.filter(
          (shipment) => shipment.productId === salableProduct.product.id,
        )

        const { product } = salableProduct

        const result = mapReplenishmentPlanItemFragment(
          product,
          productsForecast,
          productsPlanItems,
          productsShipments,
        )

        planProducts.push({
          ...result.planProduct,
          isInPlan: true,
          salableProductId: salableProduct.salableProductId,
        })

        forecastItems = {
          ...forecastItems,
          ...result.forecastItems,
        }

        plannedItems = {
          ...plannedItems,
          ...result.plannedItems,
        }

        shipmentItems = {
          ...shipmentItems,
          ...result.shipmentItems,
        }
      }

      const submittedItems: PlanItem<SubmittedItem> = await getSubmittedItems({
        branchId,
        fromMonth: fromMonthFormatted,
        toMonth: toMonthFormatted,
        productIds: planProducts.map((product) => product.id),
      })

      dispatch(
        replenishmentPlanActions.setSpecies(
          planData.planSpecies.map((species) => ({
            sanaId: species.sanaId,
            name: species.name,
            group: species.group ?? null,
          })),
        ),
      )

      dispatch(replenishmentPlanActions.setPlanProducts(planProducts))
      dispatch(replenishmentPlanActions.setForecastItems(forecastItems))
      dispatch(replenishmentPlanActions.setPlannedItems(plannedItems))
      dispatch(replenishmentPlanActions.setShipments(shipmentItems))
      dispatch(replenishmentPlanActions.setSubmittedItems(submittedItems))
    } catch (e) {
      console.error(e)
    } finally {
      dispatch(replenishmentPlanActions.setIsLoadingPlan(false))
    }
  }, [
    branchId,
    dispatch,
    fromMonthFormatted,
    getPlan,
    getPlanItemsForecastsShipments,
    getSubmittedItems,
    toMonthFormatted,
  ])

  return { fetchAndSavePlan }
}

export { useFetchAndSavePlan }
